import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocIcon,
  AutoFixHigh as ProcessIcon,
  CheckCircle as CheckIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import pandoraAdminService from '../../../services/pandoraAdminService';
import type { PandoraDocument } from '../../../services/pandoraAdminService';

const CATEGORIES = [
  { value: 'petition', label: 'Peticao', description: 'Peticoes iniciais, contestacoes, recursos' },
  { value: 'law', label: 'Legislacao', description: 'Leis, decretos, normas' },
  { value: 'legal_case', label: 'Jurisprudencia', description: 'Acordaos, decisoes, sumulas' },
  { value: 'legal_theory', label: 'Doutrina', description: 'Textos doutrinarios e academicos' },
  { value: 'verbetes', label: 'Verbetes', description: 'Termos e definicoes juridicas' },
] as const;

type CategoryValue = (typeof CATEGORIES)[number]['value'];

const PIPELINE_STEPS = [
  'Upload',
  'Classificacao',
  'Segmentacao',
  'Validacao',
  'Promocao',
];

export default function PandoraUploadPage() {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<CategoryValue>('petition');
  const [autoProcess, setAutoProcess] = useState(true);

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'file'>('text');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<PandoraDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (inputMode === 'text') {
      if (!title.trim()) { setError('Informe o titulo do documento.'); return; }
      if (!content.trim()) { setError('Cole o conteudo do documento.'); return; }
    } else {
      if (!file) { setError('Selecione um arquivo.'); return; }
    }

    setLoading(true);
    try {
      let doc: PandoraDocument;
      if (inputMode === 'text') {
        doc = await pandoraAdminService.uploadText({
          title: title.trim(),
          content: content.trim(),
          category,
          auto_process: autoProcess,
        });
      } else {
        doc = await pandoraAdminService.uploadFile(
          file!,
          category,
          title.trim() || undefined,
          autoProcess,
        );
      }
      setSuccess(doc);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Erro ao enviar documento.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      const ext = selected.name.split('.').pop()?.toLowerCase();
      if (['txt', 'docx', 'pdf'].includes(ext || '')) {
        setFile(selected);
        if (!title.trim()) setTitle(selected.name.replace(/\.[^.]+$/, ''));
        setInputMode('file');
      } else {
        setError('Formato nao suportado. Use .txt, .docx ou .pdf');
      }
    }
    e.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      const ext = dropped.name.split('.').pop()?.toLowerCase();
      if (['txt', 'docx', 'pdf'].includes(ext || '')) {
        setFile(dropped);
        if (!title) setTitle(dropped.name.replace(/\.[^.]+$/, ''));
        setInputMode('file');
      } else {
        setError('Formato nao suportado. Use .txt, .docx ou .pdf');
      }
    }
  }, [title]);

  const clearFile = () => {
    setFile(null);
    setInputMode('text');
  };

  const handleNewUpload = () => {
    setSuccess(null);
    setError(null);
    setTitle('');
    setContent('');
    setFile(null);
    setInputMode('text');
  };

  const charCount = content.length;
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  if (success) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Documento enviado com sucesso
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            <strong>{success.title}</strong>
          </Typography>
          <Chip
            label={CATEGORIES.find(c => c.value === success.category)?.label || success.category}
            size="small"
            sx={{ mb: 3 }}
          />
          {autoProcess && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              O pipeline de classificacao e segmentacao foi iniciado automaticamente.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate(`/admin/pandora/documents/${success.id}`)}
              endIcon={<ArrowIcon />}
            >
              Ver documento
            </Button>
            <Button variant="outlined" onClick={handleNewUpload}>
              Enviar outro
            </Button>
            <Button variant="text" onClick={() => navigate('/admin/pandora/documents')}>
              Lista de documentos
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Upload de Documento
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Envie textos juridicos para o pipeline de validacao. Apos o upload, o documento
          sera classificado, segmentado em paragrafos e preparado para validacao por advogados.
        </Typography>
      </Box>

      {/* Pipeline indicator */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stepper activeStep={0} alternativeLabel>
            {PIPELINE_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Content input */}
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Mode toggle + file drop zone */}
              <Box
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                sx={{
                  border: isDragging ? '2px dashed' : 'none',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  bgcolor: isDragging ? 'action.hover' : 'transparent',
                  transition: 'all 0.2s',
                  mb: 2,
                }}
              >
                {file ? (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid',
                    borderColor: 'primary.200', mb: 2,
                  }}>
                    <FileIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                    <Tooltip title="Remover arquivo e usar texto">
                      <IconButton size="small" onClick={clearFile}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={18}
                    placeholder="Cole aqui o texto do documento juridico...&#10;&#10;Voce tambem pode arrastar um arquivo .txt, .docx ou .pdf para esta area."
                    value={content}
                    onChange={(e) => { setContent(e.target.value); setInputMode('text'); }}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Georgia", "Times New Roman", serif',
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                      },
                    }}
                  />
                )}
              </Box>

              {/* Footer: char count + file upload button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {inputMode === 'text' ? (
                  <Typography variant="caption" color="text.secondary">
                    {wordCount > 0 ? `${wordCount.toLocaleString()} palavras | ${charCount.toLocaleString()} caracteres` : 'Nenhum conteudo'}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Arquivo selecionado: {file?.name}
                  </Typography>
                )}
                <Box>
                  <input
                    type="file"
                    accept=".txt,.docx,.pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="pandora-file-input"
                  />
                  <label htmlFor="pandora-file-input">
                    <Button component="span" size="small" startIcon={<UploadIcon />} variant="text">
                      Upload arquivo
                    </Button>
                  </label>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Settings */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dados do Documento
              </Typography>

              <TextField
                fullWidth
                label="Titulo"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                size="small"
                sx={{ mb: 2.5 }}
                helperText="Nome descritivo para identificacao"
              />

              <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={category}
                  label="Categoria"
                  onChange={(e) => setCategory(e.target.value as CategoryValue)}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      <Box>
                        <Typography variant="body2">{c.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Processamento
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={autoProcess}
                    onChange={(e) => setAutoProcess(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Pipeline automatico</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Classificar e segmentar apos upload
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, ml: 0 }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading || (inputMode === 'text' ? !content.trim() : !file)}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ProcessIcon />}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Processando...' : 'Enviar para Pandora'}
              </Button>
            </CardContent>
          </Card>

          {/* Info card */}
          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" fontWeight={600} gutterBottom display="block">
                Formatos aceitos
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip label=".txt" size="small" variant="outlined" />
                <Chip label=".docx" size="small" variant="outlined" />
                <Chip label=".pdf" size="small" variant="outlined" />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                O pipeline ira extrair metadados, dividir o texto em paragrafos
                e classificar cada trecho com a taxonomia tripartida (estrutural,
                teorico, factual) para posterior validacao.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
